/*
  Execution tab pieces.
  Wave 1 (screen 09): ExecutionColumnsMenu ("Columns 7/15"), ExecutionProgressCell ("20/24" + bar).
  Wave 2 (screen 09):
  - ExecutionTable          — read-first rows on the foundation DataTable: status pill, progress, category,
                              Done by avatar + name, dates; a click on a cell edits it (#1). Levels show by
                              indent only (no "L1" badge). Only open parents' sub-items are built and the
                              table renders the first 100 rows + "Show more" (#9).
  - ExecutionStatusSummary  — main tasks by status + "no due dates" hint with a link to Timeline.
  - ExecutionBulkBar        — Set status · Assign · Set dates · Export · Print for the selection (#4).
  - SaveCountPill           — the "3" inside "Save execution" (#6).
*/
import { forwardRef, useEffect, useId, useMemo, useRef, useState } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import {
  ArrowRight, CalendarDays, ChevronDown, ChevronRight, CircleDot, Columns3, Download, Printer, RotateCcw, UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DataTable } from '@/components/ui/data-table';
import { StatusPill, ToneDot } from '@/components/ui/status-pill';
import { TableCheckbox } from '@/components/ui/table-checkbox';
import { Avatar } from '@/components/ui/avatar-initials';
import { BulkBar, BulkButton } from '@/components/ui/bulk-bar';
import { FilterBar } from '@/components/ui/filter-bar';
import { getStatusTone } from '@/lib/status';
import { cn } from '@/lib/utils';
import { formatDate, formatNumber } from '@/lib/format';
import { ActionMenu, ProgressBar } from './ProjectUi';
import { DEFAULT_EXECUTION_COLUMNS, EXECUTION_COLUMNS, getChildProgress, isIncompleteDate } from './projectDetailModel';
import { EXECUTION_CATEGORY_OPTIONS, EXECUTION_STATUS_OPTIONS, splitPeople } from './executionModel';

export function ExecutionColumnsMenu({ value = [], onChange }) {
  const visible = new Set(value);
  const toggle = (key, checked) => {
    const next = new Set(value);
    if (checked) next.add(key);
    else next.delete(key);
    onChange(EXECUTION_COLUMNS.filter((column) => column.always || next.has(column.key)).map((column) => column.key));
  };
  const isDefault = value.length === DEFAULT_EXECUTION_COLUMNS.length && DEFAULT_EXECUTION_COLUMNS.every((key) => visible.has(key));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" data-testid="execution-columns-button">
          <Columns3 aria-hidden="true" />
          Columns {value.length}/{EXECUTION_COLUMNS.length}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[264px] rounded-xl border-line bg-surface p-2 text-ink shadow-overlay">
        <p className="px-2 pb-1.5 pt-1 text-[11px] font-bold uppercase tracking-[0.04em] text-ink-3">Show columns</p>
        <ul className="max-h-[min(488px,calc(100dvh-180px))] overflow-y-auto">
          {EXECUTION_COLUMNS.map((column) => {
            const id = `execution-column-${column.key}`;
            return (
              <li key={column.key}>
                <label
                  htmlFor={id}
                  className={cn(
                    'flex h-8 cursor-pointer items-center gap-2.5 rounded-lg px-2 text-[13px] hover:bg-surface-hover',
                    column.always && 'cursor-default text-ink-3 hover:bg-transparent'
                  )}
                >
                  <TableCheckbox
                    id={id}
                    checked={column.always || visible.has(column.key)}
                    disabled={column.always}
                    onCheckedChange={(checked) => toggle(column.key, Boolean(checked))}
                  />
                  <span className="flex-1">{column.label}</span>
                  {column.always ? <span className="text-[11px]">Always</span> : null}
                </label>
              </li>
            );
          })}
        </ul>
        <div className="mt-1 border-t border-line pt-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            disabled={isDefault}
            onClick={() => onChange(DEFAULT_EXECUTION_COLUMNS)}
          >
            <RotateCcw aria-hidden="true" />
            Reset to the 7 defaults
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** "20/24" + bar from the direct sub-items (or a precomputed { done, total, notRequired }). */
export function ExecutionProgressCell({ subItems, progress: given }) {
  const progress = given || getChildProgress(subItems);
  if (!progress) return <span className="text-xs text-ink-4" aria-label="No sub-items">—</span>;
  if (progress.notRequired) return <span className="text-[12px] text-ink-3">n/a</span>;
  const percent = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const tone = progress.done === progress.total ? 'navy' : (percent < 40 ? 'amber' : 'blue');
  return (
    <div className="flex items-center gap-2" title={`${progress.done} of ${progress.total} sub-items done`}>
      <ProgressBar value={percent} tone={tone} className="w-[60px] shrink-0" label="Sub-items done" />
      <span className="text-[12px] font-semibold tabular-nums text-ink-2">{progress.done}/{progress.total}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ editable cells (#1) */

const Dash = () => (
  <>
    <span className="text-ink-4" aria-hidden="true">—</span>
    <span className="sr-only">None</span>
  </>
);

/** A cell value that is a button: quiet until hovered / focused, then a soft outline and a chevron. */
const CellButton = forwardRef(function CellButton({ children, className, chevron = true, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'group/cell -mx-1.5 inline-flex max-w-full items-center gap-1.5 rounded-lg px-1.5 py-1 text-left text-[13px] text-ink transition-colors',
        'hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-surface-hover',
        className
      )}
      {...props}
    >
      {children}
      {chevron ? (
        // shown on hover / focus only, so it takes no room from the value the rest of the time
        <ChevronDown className="hidden h-3.5 w-3.5 shrink-0 text-ink-4 group-hover/cell:inline-block group-focus-visible/cell:inline-block group-data-[state=open]/cell:inline-block" aria-hidden="true" />
      ) : null}
    </button>
  );
});

/** Keeps Escape / arrow keys inside a popover from reaching the row, the table or the bulk bar. */
const stopKeys = (event) => event.stopPropagation();

const ToneIcon = (tone) => function StatusToneIcon() {
  return <ToneDot tone={tone} className="mx-[3.5px]" />;
};

export function ExecutionStatusCell({ status, title, canEdit, onChange }) {
  const value = status || 'Not Started';
  const pill = <StatusPill status={value} domain="execution" size="sm" />;
  if (!canEdit) return pill;
  return (
    <ActionMenu
      label={`Status of ${title}`}
      align="start"
      trigger={<CellButton aria-label={`Status of ${title}: ${value}`}>{pill}</CellButton>}
      items={EXECUTION_STATUS_OPTIONS.map((option) => ({
        key: option,
        label: option,
        icon: ToneIcon(getStatusTone(option, 'execution')),
        hint: option === value ? 'Current' : undefined,
        onSelect: () => { if (option !== value) onChange(option); },
      }))}
    />
  );
}

export function ExecutionCategoryCell({ category, title, canEdit, onChange }) {
  const value = category || '';
  const shown = value ? <span className="truncate" title={value}>{value}</span> : <Dash />;
  if (!canEdit) return <span className="text-[13px] text-ink-2">{shown}</span>;
  return (
    <ActionMenu
      label={`Category of ${title}`}
      align="start"
      trigger={<CellButton aria-label={`Category of ${title}: ${value || 'none'}`} className="text-ink-2">{shown}</CellButton>}
      items={EXECUTION_CATEGORY_OPTIONS.map((option) => ({
        key: option,
        label: option,
        hint: option === value ? 'Current' : undefined,
        onSelect: () => { if (option !== value) onChange(option); },
      }))}
    />
  );
}

/** Check-box list of people with a filter box above 8 names. `value` / onChange use arrays of names. */
export function PeoplePicker({ value = [], onChange, options = [], label = 'People' }) {
  const [query, setQuery] = useState('');
  const all = useMemo(() => [...new Set([...options, ...value])], [options, value]);
  const needle = query.trim().toLowerCase();
  const shown = needle ? all.filter((name) => name.toLowerCase().includes(needle)) : all;
  const toggle = (name, checked) => onChange(checked ? [...value.filter((item) => item !== name), name] : value.filter((item) => item !== name));
  return (
    <div className="space-y-1.5" role="group" aria-label={label}>
      {all.length > 8 ? (
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Find a person…"
          aria-label={`Find in ${label}`}
          className="h-8 rounded-lg text-[13px]"
        />
      ) : null}
      <ul className="max-h-[260px] overflow-y-auto">
        {shown.map((name) => {
          const checked = value.includes(name);
          return (
            <li key={name}>
              <label className="flex h-8 cursor-pointer items-center gap-2.5 rounded-lg px-2 text-[13px] hover:bg-surface-hover">
                <TableCheckbox checked={checked} onCheckedChange={(next) => toggle(name, next)} aria-label={name} />
                <Avatar name={name} size={20} />
                <span className="min-w-0 flex-1 truncate">{name}</span>
              </label>
            </li>
          );
        })}
        {!shown.length ? <li className="px-2 py-2 text-[12.5px] text-ink-3">No one matches “{query}”.</li> : null}
      </ul>
    </div>
  );
}

function PeopleSummary({ people }) {
  if (!people.length) return null;
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar name={people[0]} size={22} />
      <span className="min-w-0 truncate text-[13px] text-ink">{people[0]}</span>
      {people.length > 1 ? <span className="shrink-0 text-[12px] font-semibold text-ink-3" title={people.slice(1).join(', ')}>+{people.length - 1}</span> : null}
    </span>
  );
}

/** Done by / Checked by: avatar + name; edits in a popover and commits ONCE when it closes. */
export function ExecutionPeopleCell({ value, title, fieldLabel = 'Done by', canEdit, options, onCommit, showAssign = false, assignOnHover = false, placeholder }) {
  const people = useMemo(() => splitPeople(value), [value]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(people);
  const onOpenChange = (next) => {
    if (next) setDraft(people);
    else if (draft.join(', ') !== people.join(', ')) onCommit(draft.join(', '));
    setOpen(next);
  };
  const shown = people.length ? <PeopleSummary people={people} /> : (
    showAssign
      ? (
        <span className="text-[13px] text-ink-3">
          Unassigned
          {canEdit ? <span className={cn('ml-1.5 font-semibold text-brand-ink', assignOnHover && '[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/row:opacity-100 [@media(hover:hover)]:group-focus-within/row:opacity-100')}>Assign</span> : null}
        </span>
      )
      : (placeholder ? <span className="text-[12.5px] text-ink-3">{placeholder}</span> : <Dash />)
  );
  if (!canEdit) return shown;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <CellButton aria-label={`${fieldLabel} of ${title}: ${people.join(', ') || 'nobody'}`} chevron={Boolean(people.length)}>
          {shown}
        </CellButton>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[260px] rounded-xl border-line bg-surface p-2 text-ink shadow-overlay" onKeyDown={stopKeys}>
        <p className="px-2 pb-1.5 pt-1 text-[11px] font-bold uppercase tracking-[0.04em] text-ink-3">{fieldLabel}</p>
        <PeoplePicker value={draft} onChange={setDraft} options={options} label={`${fieldLabel} of ${title}`} />
        <div className="mt-1.5 flex items-center justify-between border-t border-line-2 px-1 pt-1.5">
          <Button type="button" variant="ghost" size="xs" onClick={() => setDraft([])} disabled={!draft.length}>Clear</Button>
          <Button type="button" size="xs" onClick={() => onOpenChange(false)}>Done</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Shown under a date field when the date cannot be meant (a 1- to 3-digit year typed as 0002, …). */
export const INCOMPLETE_DATE_MESSAGE = 'Enter the full date — a 4-digit year from 1900.';

/**
 * A date shown as "15 Jan 2027"; edits in a popover (date field, Clear, Save).
 * An incomplete date (isIncompleteDate: "15/01/2" arrives as 0002-01-15) is never committed — on a parent it
 * would be copied to every sub-item — the popover stays open with the message instead.
 */
export function ExecutionDateCell({ value, title, fieldLabel = 'Due date', canEdit, onCommit, prefix, placeholder }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [error, setError] = useState('');
  const errorId = useId();
  const shown = value
    ? <span className="whitespace-nowrap tabular-nums">{prefix ? `${prefix} ` : ''}{formatDate(value)}</span>
    : (placeholder ? <span className="text-[12.5px] text-ink-3">{placeholder}</span> : <Dash />);
  if (!canEdit) return <span className="text-[13px] text-ink-2">{shown}</span>;
  const save = (next) => {
    if (next && isIncompleteDate(next)) {
      setError(INCOMPLETE_DATE_MESSAGE);
      return;
    }
    setOpen(false);
    if ((next || '') !== (value || '')) onCommit(next || '');
  };
  return (
    <Popover open={open} onOpenChange={(next) => { if (next) { setDraft(value || ''); setError(''); } setOpen(next); }}>
      <PopoverTrigger asChild>
        <CellButton aria-label={`${fieldLabel} of ${title}: ${value ? formatDate(value) : 'not set'}`} className="text-ink-2" chevron={false}>
          {shown}
          <CalendarDays className="hidden h-3.5 w-3.5 shrink-0 text-ink-4 group-hover/cell:inline-block group-focus-visible/cell:inline-block" aria-hidden="true" />
        </CellButton>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[244px] rounded-xl border-line bg-surface p-3 text-ink shadow-overlay" onKeyDown={stopKeys}>
        <form onSubmit={(event) => { event.preventDefault(); save(draft); }} className="space-y-2.5">
          <label className="block space-y-1">
            <span className="text-[12px] font-semibold text-ink-3">{fieldLabel}</span>
            <Input
              type="date"
              value={draft}
              onChange={(event) => { setDraft(event.target.value); setError(''); }}
              aria-label={`${fieldLabel} of ${title}`}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              className="h-9 rounded-lg"
              autoFocus
            />
          </label>
          {error ? <p id={errorId} className="text-[12px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          <div className="flex items-center justify-between">
            <Button type="button" variant="ghost" size="xs" onClick={() => save('')} disabled={!value}>Clear date</Button>
            <Button type="submit" size="xs">Save</Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/** Free text (zones, device type, comments): shown as text, edited in a popover. */
export function ExecutionTextCell({ value, title, fieldLabel, canEdit, onCommit, multiline = false, muted = false }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const shown = value ? <span className={cn('truncate', muted && 'text-ink-3')} title={value}>{value}</span> : <Dash />;
  if (!canEdit) return <span className="block truncate text-[13px] text-ink-2">{shown}</span>;
  const save = () => {
    setOpen(false);
    if (draft.trim() !== String(value || '').trim()) onCommit(draft.trim());
  };
  return (
    <Popover open={open} onOpenChange={(next) => { if (next) setDraft(value || ''); setOpen(next); }}>
      <PopoverTrigger asChild>
        <CellButton aria-label={`${fieldLabel} of ${title}: ${value || 'empty'}`} className="w-full text-ink-2" chevron={false}>
          {shown}
        </CellButton>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] rounded-xl border-line bg-surface p-3 text-ink shadow-overlay" onKeyDown={stopKeys}>
        <form onSubmit={(event) => { event.preventDefault(); save(); }} className="space-y-2.5">
          <label className="block space-y-1">
            <span className="text-[12px] font-semibold text-ink-3">{fieldLabel}</span>
            {multiline ? (
              <Textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) save(); }}
                aria-label={`${fieldLabel} of ${title}`}
                className="min-h-[88px] rounded-lg text-[13px]"
                autoFocus
              />
            ) : (
              <Input value={draft} onChange={(event) => setDraft(event.target.value)} aria-label={`${fieldLabel} of ${title}`} className="h-9 rounded-lg" autoFocus />
            )}
          </label>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="xs" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" size="xs">Save</Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/* ------------------------------------------------------------------ table (#1, #9) */

const COLUMN_WIDTHS = {
  // 09 #1: 7 default columns + a 280px Task fit 1,128px; long categories / zones truncate with a tooltip
  status: 136, progress: 128, category: 170, done_by: 140, due_date: 108, zone: 136,
  start_date: 108, checked_by: 170, comments: 220, monitoring: 104, zone_2: 120, zone_3: 120, device_type: 150, updated: 112,
};

function TaskCell({ row, onToggle }) {
  const indent = row.depth * 28;
  const strong = row.depth === 0;
  return (
    <div className="flex min-w-0 items-center gap-1.5" style={{ paddingLeft: indent }}>
      {row.hasChildren ? (
        <button
          type="button"
          onClick={() => onToggle(row.id)}
          aria-expanded={row.expanded}
          aria-label={`${row.expanded ? 'Collapse' : 'Expand'} ${row.title}`}
          className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-ink-3 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {row.expanded ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
        </button>
      ) : <span className="w-6 shrink-0" aria-hidden="true" />}
      <span
        className={cn('min-w-0 truncate', strong ? 'text-[13.5px] font-semibold text-ink' : 'text-[13px] font-medium text-ink-2')}
        title={row.title}
      >
        {row.title || 'Untitled task'}
      </span>
    </div>
  );
}

/**
 * The Execution table. `rows` from buildExecutionRows (open parents only); `columnKeys` = the visible
 * columns (Task always first). Edits go to onEdit(taskId, field, value); selection is controlled.
 */
export function ExecutionTable({
  rows,
  columnKeys,
  canEdit,
  assignees,
  selectedIds,
  onSelectionChange,
  onToggle,
  onEdit,
  pagingKey,
  empty,
}) {
  // Handlers change every render of the page; the columns read them through a ref so the columns (and
  // every memoised row) stay the same while only the data changes.
  const handlers = useRef({ onToggle, onEdit });
  handlers.current = { onToggle, onEdit };
  const keys = columnKeys.join('|');
  const columns = useMemo(() => {
    const edit = (row, field) => (value) => handlers.current.onEdit(row.id, field, value);
    const defs = {
      task: {
        key: 'task',
        header: 'Task',
        minWidth: 280,
        hideable: false,
        // 09 #2: long device titles ellipsize (title tooltip) instead of widening the table past the page
        truncate: true,
        render: (row) => <TaskCell row={row} onToggle={(id) => handlers.current.onToggle(id)} />,
      },
      status: {
        key: 'status',
        header: 'Status',
        width: COLUMN_WIDTHS.status,
        minWidth: 128,
        render: (row) => <ExecutionStatusCell status={row.task.status} title={row.title} canEdit={canEdit} onChange={edit(row, 'status')} />,
      },
      progress: {
        key: 'progress',
        header: 'Progress',
        width: COLUMN_WIDTHS.progress,
        minWidth: 118,
        render: (row) => <ExecutionProgressCell progress={row.progress} />,
      },
      category: {
        key: 'category',
        header: 'Category',
        width: COLUMN_WIDTHS.category,
        render: (row) => <ExecutionCategoryCell category={row.task.category} title={row.title} canEdit={canEdit} onChange={edit(row, 'category')} />,
      },
      done_by: {
        key: 'done_by',
        header: 'Done by',
        width: COLUMN_WIDTHS.done_by,
        render: (row) => (
          <ExecutionPeopleCell
            value={row.task.done_by}
            title={row.title}
            canEdit={canEdit}
            options={assignees}
            onCommit={edit(row, 'done_by')}
            showAssign={row.depth === 0 && !['Done', 'Not required'].includes(row.task.status || 'Not Started')}
            assignOnHover
          />
        ),
      },
      checked_by: {
        key: 'checked_by',
        header: 'Checked by',
        width: COLUMN_WIDTHS.checked_by,
        render: (row) => <ExecutionPeopleCell value={row.task.checked_by} title={row.title} fieldLabel="Checked by" canEdit={canEdit} options={assignees} onCommit={edit(row, 'checked_by')} />,
      },
      start_date: {
        key: 'start_date',
        header: 'Start',
        width: COLUMN_WIDTHS.start_date,
        minWidth: 100,
        render: (row) => <ExecutionDateCell value={row.task.start_date} title={row.title} fieldLabel="Start date" canEdit={canEdit} onCommit={edit(row, 'start_date')} />,
      },
      due_date: {
        key: 'due_date',
        header: 'Due',
        width: COLUMN_WIDTHS.due_date,
        // a date never squeezes to '15 J…': below this the table scrolls (Task stays pinned)
        minWidth: 100,
        render: (row) => <ExecutionDateCell value={row.task.due_date} title={row.title} fieldLabel="Due date" canEdit={canEdit} onCommit={edit(row, 'due_date')} />,
      },
      zone: {
        key: 'zone',
        header: 'Zone',
        width: COLUMN_WIDTHS.zone,
        render: (row) => (row.zones && !row.task.zone
          ? <span className="whitespace-nowrap text-[12.5px] text-ink-3">{formatNumber(row.zones, { decimals: 0 })} building{row.zones === 1 ? '' : 's'}</span>
          : <ExecutionTextCell value={row.task.zone} title={row.title} fieldLabel="Zone" canEdit={canEdit} onCommit={edit(row, 'zone')} />),
      },
      comments: {
        key: 'comments',
        header: 'Comments',
        width: COLUMN_WIDTHS.comments,
        render: (row) => <ExecutionTextCell value={row.task.comments} title={row.title} fieldLabel="Comments" multiline canEdit={canEdit} onCommit={edit(row, 'comments')} />,
      },
      monitoring: {
        key: 'monitoring',
        header: 'Monitoring',
        width: COLUMN_WIDTHS.monitoring,
        align: 'center',
        render: (row) => (
          <span className="inline-flex justify-center">
            <TableCheckbox
              checked={Boolean(row.task.monitoring)}
              disabled={!canEdit}
              onCheckedChange={(next) => handlers.current.onEdit(row.id, 'monitoring', Boolean(next))}
              aria-label={`Monitoring for ${row.title}`}
            />
          </span>
        ),
      },
      zone_2: {
        key: 'zone_2',
        header: 'Zone 2',
        width: COLUMN_WIDTHS.zone_2,
        render: (row) => <ExecutionTextCell value={row.task.zone_2} title={row.title} fieldLabel="Zone 2" canEdit={canEdit} onCommit={edit(row, 'zone_2')} />,
      },
      zone_3: {
        key: 'zone_3',
        header: 'Zone 3',
        width: COLUMN_WIDTHS.zone_3,
        render: (row) => <ExecutionTextCell value={row.task.zone_3} title={row.title} fieldLabel="Zone 3" canEdit={canEdit} onCommit={edit(row, 'zone_3')} />,
      },
      device_type: {
        key: 'device_type',
        header: 'Device type',
        width: COLUMN_WIDTHS.device_type,
        render: (row) => <ExecutionTextCell value={row.task.device_type} title={row.title} fieldLabel="Device type" canEdit={canEdit} onCommit={edit(row, 'device_type')} />,
      },
      updated: {
        key: 'updated',
        header: 'Updated',
        width: COLUMN_WIDTHS.updated,
        render: (row) => (row.task.updated ? <span className="whitespace-nowrap text-[12.5px] text-ink-3">{formatDate(row.task.updated)}</span> : <Dash />),
      },
    };
    return keys.split('|').map((key) => defs[key]).filter(Boolean);
  }, [keys, canEdit, assignees]);

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey="id"
      manualSort
      selectable
      selectedKeys={selectedIds}
      onSelectionChange={onSelectionChange}
      getRowLabel={(row) => row.title}
      rowTestId={(row) => `execution-row-${row.id}`}
      rowClassName={(row) => (row.depth > 0 ? 'execution-subrow' : undefined)}
      onRowOpen={(row) => { if (row.hasChildren) handlers.current.onToggle(row.id); }}
      renderBulkBar={() => null}
      // 09 #2: the checkbox + Task stay pinned while the other columns scroll sideways. A selected row's
      // brand-soft is see-through in dark mode: paint it over the surface so the pinned cells stay opaque.
      stickyFirstColumn
      className="[&_tr[data-state=selected]>td.sticky]:bg-surface [&_tr[data-state=selected]>td.sticky]:[background-image:linear-gradient(var(--brand-soft),var(--brand-soft))]"
      pagingKey={pagingKey}
      ariaLabel="Execution tasks"
      empty={empty}
      testId="execution-table"
    />
  );
}

/**
 * Phones (09 mobile): one card per row — name, status (tap to change), progress, category, Done by, Due —
 * sub-items as lighter cards under an open parent; the first 60 rows, then "Show more".
 */
export function ExecutionCards({ rows, canEdit, assignees, selectedIds = [], onSelectionChange, onToggle, onEdit, pagingKey, empty }) {
  const step = 60;
  const [limit, setLimit] = useState(step);
  useEffect(() => { setLimit(step); }, [pagingKey]);
  const selected = new Set(selectedIds);
  const toggleSelected = (id, next) => onSelectionChange(next ? [...selectedIds.filter((entry) => entry !== id), id] : selectedIds.filter((entry) => entry !== id));
  if (!rows.length) return empty || null;
  const shown = rows.slice(0, limit);
  return (
    <div data-testid="execution-cards">
      <ul className="space-y-2">
        {shown.map((row) => {
          const isSelected = selected.has(row.id);
          const edit = (field) => (value) => onEdit(row.id, field, value);
          const open = !['Done', 'Not required'].includes(row.task.status || 'Not Started');
          const hasPeople = Boolean(String(row.task.done_by || '').trim());
          return (
            <li
              key={row.id}
              data-testid={`execution-card-${row.id}`}
              style={{ marginLeft: Math.min(row.depth, 3) * 14 }}
              className={cn(
                'rounded-card border px-3 py-2.5',
                row.depth ? 'border-line-2 bg-surface-2' : 'border-line bg-surface shadow-card',
                isSelected && 'border-[color:var(--brand)] bg-brand-soft'
              )}
            >
              <div className="flex items-start gap-2.5">
                <TableCheckbox checked={isSelected} onCheckedChange={(next) => toggleSelected(row.id, next)} aria-label={`Select ${row.title}`} className="mt-1" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    {row.hasChildren ? (
                      <button
                        type="button"
                        onClick={() => onToggle(row.id)}
                        aria-expanded={row.expanded}
                        aria-label={`${row.expanded ? 'Collapse' : 'Expand'} ${row.title}`}
                        className="flex min-w-0 items-start gap-1 text-left"
                      >
                        {row.expanded ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" /> : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />}
                        <span className={cn('min-w-0 break-words', row.depth ? 'text-[13.5px] font-semibold text-ink-2' : 'text-[15px] font-bold text-ink')}>{row.title}</span>
                      </button>
                    ) : (
                      <span className={cn('min-w-0 break-words pl-5', row.depth ? 'text-[13.5px] font-semibold text-ink-2' : 'text-[15px] font-bold text-ink')}>{row.title}</span>
                    )}
                    <ExecutionStatusCell status={row.task.status} title={row.title} canEdit={canEdit} onChange={edit('status')} />
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 pl-5 text-[12.5px]">
                    {row.progress ? <ExecutionProgressCell progress={row.progress} /> : null}
                    <ExecutionCategoryCell category={row.task.category} title={row.title} canEdit={canEdit} onChange={edit('category')} />
                    {hasPeople || (canEdit && open) ? (
                      <ExecutionPeopleCell
                        value={row.task.done_by}
                        title={row.title}
                        canEdit={canEdit}
                        options={assignees}
                        onCommit={edit('done_by')}
                        showAssign={row.depth === 0 && open}
                        placeholder="Add Done by"
                      />
                    ) : null}
                    {row.task.due_date || (canEdit && open) ? (
                      <ExecutionDateCell value={row.task.due_date} title={row.title} fieldLabel="Due date" canEdit={canEdit} onCommit={edit('due_date')} prefix="Due" placeholder="Set due" />
                    ) : null}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {rows.length > limit ? (
        <div className="mt-2 flex items-center justify-between gap-2 px-1 text-[12.5px] text-ink-3" data-testid="execution-cards-more">
          <span>Showing {formatNumber(limit, { decimals: 0 })} of {formatNumber(rows.length, { decimals: 0 })}</span>
          <Button type="button" variant="outline" size="xs" onClick={() => setLimit((current) => current + step)}>Show {Math.min(step, rows.length - limit)} more</Button>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ search (#9) */

/**
 * The Execution FilterBar with its search text kept here: typing re-renders only this bar; `onQueryChange(q)`
 * runs after a `delay` ms pause (the page writes ?ex.q and filters then). A new `query` from outside (Clear all,
 * a saved view, back / forward) replaces the text. Other props go to FilterBar unchanged.
 */
export function ExecutionFilterBar({ query = '', onQueryChange, delay = 250, search = {}, onClearAll, ...props }) {
  const [text, setText] = useState(query);
  const sentRef = useRef(query);
  const sendRef = useRef(onQueryChange);
  sendRef.current = onQueryChange;
  useEffect(() => {
    if (query === sentRef.current) return;
    sentRef.current = query;
    setText(query);
  }, [query]);
  useEffect(() => {
    if (text === sentRef.current) return undefined;
    const timer = window.setTimeout(() => {
      sentRef.current = text;
      sendRef.current?.(text);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [text, delay]);
  return (
    <FilterBar
      {...props}
      search={{ ...search, value: text, onChange: setText }}
      onClearAll={onClearAll ? () => {
        // a text not handed over yet must not come back after Clear all
        sentRef.current = '';
        setText('');
        onClearAll();
      } : undefined}
    />
  );
}

/* ------------------------------------------------------------------ summary bar */

// Status tokens (they have dark-theme values): --navy has none and vanished on the dark card.
const STACK_FILL = {
  navy: 'var(--status-navy)',
  blue: 'var(--status-blue)',
  amber: 'var(--status-amber)',
  red: 'var(--status-red)',
  gray: 'var(--status-gray-bg)',
  outline: 'color-mix(in srgb, var(--status-navy) 32%, transparent)',
};

/** "14 tasks ▬▬▬ Done 11 · Not required 2 · Not started 1 · Bottleneck 0 · Waiting 0  |  No due dates · Plan in Timeline →" */
export function ExecutionStatusSummary({ breakdown = [], total = 0, subItems = 0, datedCount = 0, onOpenTimeline, compact = false }) {
  const counted = breakdown.reduce((sum, entry) => sum + entry.count, 0) || 1;
  const legend = breakdown.filter((entry) => entry.count > 0 || ['blocked', 'waiting'].includes(entry.key));
  if (compact) {
    // Phones (09 #8): one line — "14 tasks · 11 done · 1 in progress · 2 not required".
    const parts = breakdown.filter((entry) => entry.count > 0);
    return (
      <p className="flex min-w-0 items-center gap-2 px-1 text-[12.5px] text-ink-3" data-testid="execution-summary">
        <span className="flex h-1.5 w-12 shrink-0 gap-[1px] overflow-hidden rounded-full bg-[color:var(--status-gray-bg)]" aria-hidden="true">
          {parts.map((entry) => (
            <span key={entry.key} className="h-full" style={{ width: `${(entry.count / counted) * 100}%`, background: STACK_FILL[entry.tone] }} />
          ))}
        </span>
        <span className="min-w-0 truncate">
          <b className="font-bold text-ink">{formatNumber(total, { decimals: 0 })} task{total === 1 ? '' : 's'}</b>
          {parts.map((entry) => (
            <span key={entry.key}> · <b className="font-semibold tabular-nums text-ink-2">{formatNumber(entry.count, { decimals: 0 })}</b> {entry.label.toLowerCase()}</span>
          ))}
        </span>
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5 rounded-card border border-line bg-surface px-4 py-3 shadow-card sm:px-5" data-testid="execution-summary">
      <span className="whitespace-nowrap text-[13px] font-bold text-ink">
        {formatNumber(total, { decimals: 0 })} task{total === 1 ? '' : 's'}
        {subItems ? <span className="ml-1.5 font-medium text-ink-3">· {formatNumber(subItems, { decimals: 0 })} sub-items</span> : null}
      </span>
      <span
        className="flex h-2 min-w-[120px] max-w-[180px] flex-1 gap-[2px] overflow-hidden rounded-full bg-[color:var(--status-gray-bg)]"
        role="img"
        aria-label={breakdown.filter((entry) => entry.count).map((entry) => `${entry.count} ${entry.label}`).join(', ') || 'No tasks'}
      >
        {breakdown.filter((entry) => entry.count).map((entry) => (
          <span key={entry.key} className="h-full" style={{ width: `${(entry.count / counted) * 100}%`, background: STACK_FILL[entry.tone] }} />
        ))}
      </span>
      <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-ink-3">
        {legend.map((entry) => (
          <li key={entry.key} className="inline-flex items-center gap-1.5">
            <ToneDot tone={entry.tone === 'outline' ? 'navy' : entry.tone} className={entry.tone === 'outline' ? 'opacity-40' : undefined} />
            {entry.label}
            <b className={cn('font-bold tabular-nums', entry.count ? 'text-ink' : 'text-ink-3')}>{entry.count}</b>
          </li>
        ))}
      </ul>
      <span className="ml-auto inline-flex flex-wrap items-center gap-x-2 text-[12.5px] text-ink-3">
        <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
        {datedCount
          ? `${formatNumber(datedCount, { decimals: 0 })} task${datedCount === 1 ? ' has' : 's have'} dates`
          : 'No due dates yet'}
        {onOpenTimeline ? (
          <button type="button" onClick={onOpenTimeline} className="inline-flex items-center gap-1 font-semibold text-brand-ink hover:underline">
            {datedCount ? 'Open Timeline' : 'Plan in Timeline'}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </span>
    </div>
  );
}

/** White count pill inside a navy primary button ("Save execution 3"). */
export function SaveCountPill({ count }) {
  if (!count) return null;
  return (
    <span className="ml-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-white px-1.5 text-[11.5px] font-extrabold tabular-nums text-[color:var(--navy,#24245f)]" aria-hidden="true">
      {count > 99 ? '99+' : count}
    </span>
  );
}

/* ------------------------------------------------------------------ bulk bar (#4) */

const menuItemClass = 'relative flex h-[34px] cursor-default select-none items-center gap-2.5 rounded-lg px-2.5 text-[13px] text-ink outline-none data-[highlighted]:bg-surface-hover';
const lightPanel = 'z-50 rounded-xl border border-line bg-surface p-1.5 text-ink shadow-overlay';

function BulkStatusMenu({ onSelect, compact = false }) {
  return (
    <DropdownMenuPrimitive.Root modal={false}>
      <DropdownMenuPrimitive.Trigger asChild>
        <BulkButton icon={CircleDot} data-testid="execution-bulk-status" aria-label="Set status">
          {compact ? 'Status' : 'Set status'}
          <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        </BulkButton>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content side="top" align="start" sideOffset={8} collisionPadding={8} onKeyDown={stopKeys} className={cn(lightPanel, 'min-w-[220px]')}>
          {EXECUTION_STATUS_OPTIONS.map((status) => (
            <DropdownMenuPrimitive.Item key={status} className={menuItemClass} onSelect={() => setTimeout(() => onSelect(status), 0)}>
              <ToneDot tone={getStatusTone(status, 'execution')} />
              {status}
            </DropdownMenuPrimitive.Item>
          ))}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

function BulkAssignPopover({ options, onApply, compact = false }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState([]);
  useEffect(() => { if (open) setDraft([]); }, [open]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {compact ? (
          <BulkButton icon={UserRound} aria-label="Assign" title="Assign" className="w-[30px] justify-center px-0" data-testid="execution-bulk-assign" />
        ) : (
          <BulkButton icon={UserRound} data-testid="execution-bulk-assign">
            Assign
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </BulkButton>
        )}
      </PopoverTrigger>
      <PopoverContent side="top" align="start" sideOffset={8} className="w-[264px] rounded-xl border-line bg-surface p-2 text-ink shadow-overlay" onKeyDown={stopKeys}>
        <p className="px-2 pb-1.5 pt-1 text-[11px] font-bold uppercase tracking-[0.04em] text-ink-3">Done by</p>
        <PeoplePicker value={draft} onChange={setDraft} options={options} label="Assign selected tasks" />
        <div className="mt-1.5 flex items-center justify-end gap-2 border-t border-line-2 px-1 pt-1.5">
          <Button type="button" variant="ghost" size="xs" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            type="button"
            size="xs"
            disabled={!draft.length}
            onClick={() => { setOpen(false); onApply(draft.join(', ')); }}
            data-testid="execution-bulk-assign-apply"
          >
            Assign {draft.length ? `${draft.length} ${draft.length === 1 ? 'person' : 'people'}` : ''}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function BulkDatesPopover({ onApply, compact = false }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState('');
  const [due, setDue] = useState('');
  const [attempted, setAttempted] = useState(false);
  useEffect(() => { if (open) { setStart(''); setDue(''); setAttempted(false); } }, [open]);
  // Applied to whole selections (and their sub-items): an incomplete date must never get through.
  const incomplete = isIncompleteDate(start) || isIncompleteDate(due);
  const invalid = !incomplete && start && due && due < start;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {compact ? (
          <BulkButton icon={CalendarDays} aria-label="Set dates" title="Set dates" className="w-[30px] justify-center px-0" data-testid="execution-bulk-dates" />
        ) : (
          <BulkButton icon={CalendarDays} data-testid="execution-bulk-dates">Set dates</BulkButton>
        )}
      </PopoverTrigger>
      <PopoverContent side="top" align="start" sideOffset={8} className="w-[280px] rounded-xl border-line bg-surface p-3 text-ink shadow-overlay" onKeyDown={stopKeys}>
        <form
          className="space-y-2.5"
          onSubmit={(event) => {
            event.preventDefault();
            setAttempted(true);
            if (incomplete || invalid || (!start && !due)) return;
            setOpen(false);
            onApply({ ...(start ? { start_date: start } : {}), ...(due ? { due_date: due } : {}) });
          }}
        >
          <p className="text-[12.5px] text-ink-3">Leave a date empty to keep what each task has.</p>
          <label className="block space-y-1">
            <span className="text-[12px] font-semibold text-ink-3">Start</span>
            <Input type="date" value={start} onChange={(event) => setStart(event.target.value)} aria-label="Start date for the selected tasks" aria-invalid={attempted && isIncompleteDate(start) ? true : undefined} className="h-9 rounded-lg" />
          </label>
          <label className="block space-y-1">
            <span className="text-[12px] font-semibold text-ink-3">Due</span>
            <Input type="date" value={due} onChange={(event) => setDue(event.target.value)} aria-label="Due date for the selected tasks" aria-invalid={attempted && isIncompleteDate(due) ? true : undefined} className="h-9 rounded-lg" />
          </label>
          {attempted && incomplete ? <p className="text-[12px] font-semibold text-status-red-ink" role="alert" data-testid="execution-bulk-dates-error">{INCOMPLETE_DATE_MESSAGE}</p> : null}
          {invalid ? <p className="text-[12px] font-semibold text-status-red-ink" role="alert">Due is before start.</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="xs" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" size="xs" disabled={invalid || (!start && !due)} data-testid="execution-bulk-dates-apply">Apply dates</Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/** Navy bar for the selected rows: Set status · Assign · Set dates (edit) and Export · Print. */
export function ExecutionBulkBar({ count, canEdit, assignees, onSetStatus, onAssign, onSetDates, onExport, onPrint, onClear, compact = false }) {
  // Phones: one line at 390px — Set status, Assign and Set dates as icons, Export / Print behind ⋯.
  const outputs = [
    { key: 'export', label: 'Export', icon: Download, onSelect: onExport, testId: 'execution-bulk-export' },
    { key: 'print', label: 'Print', icon: Printer, onSelect: onPrint, testId: 'execution-bulk-print' },
  ];
  return (
    <BulkBar
      count={count}
      onClear={onClear}
      testId="execution-bulkbar"
      noun="selected"
      moreActions={compact ? outputs : []}
      // Phones: the count alone ("(2)") so the bar stays on one line at 390px.
      label={compact ? (
        <>
          <span className="grid h-[22px] min-w-[22px] place-items-center rounded-full bg-white px-1.5 text-[11.5px] font-extrabold tabular-nums text-[color:var(--navy)]" data-testid="bulkbar-count">
            {formatNumber(count, { decimals: 0 })}
          </span>
          <span className="sr-only">selected</span>
        </>
      ) : undefined}
    >
      {canEdit ? (
        <>
          <BulkStatusMenu onSelect={onSetStatus} compact={compact} />
          <BulkAssignPopover options={assignees} onApply={onAssign} compact={compact} />
          <BulkDatesPopover onApply={onSetDates} compact={compact} />
        </>
      ) : null}
      {!compact ? outputs.map((action) => (
        <BulkButton key={action.key} icon={action.icon} onClick={action.onSelect} data-testid={action.testId}>{action.label}</BulkButton>
      )) : null}
    </BulkBar>
  );
}
